// =====================================================================
// paginas/financeiro.js — Cobranças, livro-caixa, contas e conciliação.
//
// Três sub-abas. A ordem não é aleatória: "Cobranças" primeiro porque é
// onde está o dinheiro que ainda não entrou — e é isso que dá trabalho.
// O caixa é o retrato do que já aconteceu.
//
// A tela de conciliação é a resposta ao problema central da planilha: um
// pagamento que cai no caixa não diz a que mês se refere. Aqui, diz.
// =====================================================================

import { sb } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge, modal, checar } from '../ui.js';
import { moeda, data, competencia, statusCobranca, diasAtraso } from '../../../../packages/ui/format.js';

let sub = 'cobrancas';

export async function pagFinanceiro(el, estado) {
  el.innerHTML = `
    <div class="cabecalho"><h2>Financeiro</h2></div>
    <div style="display:flex;gap:8px;margin-bottom:20px" id="subabas">
      <button data-s="cobrancas">${icon('cobranca')} Cobranças</button>
      <button data-s="caixa">${icon('financeiro')} Livro-caixa</button>
      <button data-s="conciliar">${icon('ok')} Conciliação</button>
      <button data-s="contas">${icon('bank')} Contas</button>
      <button data-s="desempenho">${icon('graficos')} Desempenho</button>
    </div>
    <div id="sub"></div>`;

  el.querySelectorAll('#subabas button').forEach(b => {
    b.classList.toggle('primaria', b.dataset.s === sub);
    b.onclick = () => { sub = b.dataset.s; pagFinanceiro(el, estado); };
  });

  const alvo = el.querySelector('#sub');
  const render = { cobrancas, caixa, conciliar, contas, desempenho }[sub];
  await render(alvo, estado);
}

// ─── Cobranças ────────────────────────────────────────────────────────
async function cobrancas(el, estado) {
  const lista = checar(await sb.from('cobrancas')
    .select('*,contratos(numero,imoveis(codigo),inquilinos(nome,telefone))')
    .order('vencimento', { ascending: false })
    .limit(200));

  el.innerHTML = `
    <div class="aviso info">${icon('alerta')}
      <div>As cobranças do mês seguinte são geradas automaticamente no dia 25, com o Pix pronto
      e aviso no WhatsApp. Esta tela é para conferir e agir sobre o que fugiu do roteiro.</div>
    </div>` + tabela([
    { th: 'Competência', td: c => escapeHtml(competencia(c.competencia)) },
    { th: 'Inquilino',   td: c => escapeHtml(c.contratos?.inquilinos?.nome || '—') },
    { th: 'Imóvel',      td: c => escapeHtml(c.contratos?.imoveis?.codigo || '—') },
    { th: 'Vencimento',  td: c => data(c.vencimento) },
    { th: 'Valor', cls: 'num', td: c => moeda(c.valor_total) },
    { th: 'Encargos', cls: 'num', td: c => {
        const e = Number(c.multa || 0) + Number(c.juros || 0);
        return e > 0 ? `<span style="color:var(--vermelho)">${moeda(e)}</span>` : '—';
      } },
    { th: 'Pago', cls: 'num', td: c => Number(c.valor_pago) > 0 ? moeda(c.valor_pago) : '—' },
    { th: 'Saldo', cls: 'num', td: c => {
        const s = Number(c.valor_total) + Number(c.multa || 0) + Number(c.juros || 0) - Number(c.valor_pago || 0);
        return s > 0.05 ? `<strong>${moeda(s)}</strong>` : badge('quitado', 'verde');
      } },
    { th: 'Situação', td: c => { const s = statusCobranca(c); return badge(s.txt, s.cor); } },
    { th: '', cls: 'acoes', td: c => c.pix_copia_cola
        ? `<button class="icone" data-pix="${escapeHtml(c.pix_copia_cola)}" title="Copiar Pix">${icon('pix')}</button>` : '' }
  ], lista, 'Nenhuma cobrança ainda.');

  el.querySelectorAll('[data-pix]').forEach(b => b.onclick = () => {
    navigator.clipboard.writeText(b.dataset.pix);
    b.title = 'Pix copiado';
  });
}

// ─── Livro-caixa ──────────────────────────────────────────────────────
async function caixa(el, estado) {
  const inicio = new Date(); inicio.setMonth(inicio.getMonth() - 1);
  const de = inicio.toISOString().slice(0, 10);

  const [lanc, cats, cts] = await Promise.all([
    sb.from('lancamentos')
      .select('*,contas_financeiras(nome),categorias(nome),predios(nome)')
      .gte('data', de).order('data', { ascending: false }).limit(300),
    sb.from('categorias').select('id,nome,tipo').order('nome'),
    sb.from('contas_financeiras').select('id,nome').eq('ativa', true).order('nome')
  ]);

  const lista = checar(lanc);
  const entrada = lista.filter(l => l.tipo === 'entrada').reduce((s, l) => s + Number(l.valor), 0);
  const saida   = lista.filter(l => l.tipo === 'saida').reduce((s, l) => s + Number(l.valor), 0);
  const semCat  = lista.filter(l => !l.categoria_id).length;

  el.innerHTML = `
    <div class="grade k4" style="margin-bottom:20px">
      <div class="cartao kpi bom"><span class="rotulo">Entradas (30d)</span>
        <span class="valor">${moeda(entrada)}</span></div>
      <div class="cartao kpi"><span class="rotulo">Saídas (30d)</span>
        <span class="valor">${moeda(saida)}</span></div>
      <div class="cartao kpi ${entrada - saida < 0 ? 'alerta' : 'bom'}"><span class="rotulo">Saldo</span>
        <span class="valor">${moeda(entrada - saida)}</span></div>
      <div class="cartao kpi ${semCat ? 'alerta' : ''}"><span class="rotulo">Sem categoria</span>
        <span class="valor">${semCat}</span>
        <span class="nota">sem isso, não dá pra saber o custo de cada prédio</span></div>
    </div>
    <div style="display:flex;gap:8px;margin-bottom:16px">
      <button class="primaria" id="novo">${icon('novo')} Novo lançamento</button>
      ${semCat ? `<button id="triagem">${icon('alerta')} Categorizar ${semCat} lançamento(s)</button>` : ''}
    </div>
    <div id="tab"></div>`;

  el.querySelector('#tab').innerHTML = tabela([
    { th: 'Data',      td: l => data(l.data) },
    { th: 'Tipo',      td: l => badge(l.tipo, l.tipo === 'entrada' ? 'verde' : 'cinza') },
    { th: 'Descrição', td: l => escapeHtml(l.descricao) },
    { th: 'Categoria', td: l => l.categorias?.nome
        ? escapeHtml(l.categorias.nome) : badge('sem categoria', 'ambar') },
    { th: 'Prédio',    td: l => escapeHtml(l.predios?.nome || '—') },
    { th: 'Conta',     td: l => escapeHtml(l.contas_financeiras?.nome || '—') },
    { th: 'Valor', cls: 'num', td: l =>
        `<strong style="color:${l.tipo === 'entrada' ? 'var(--verde)' : 'var(--tinta)'}">
           ${l.tipo === 'saida' ? '−' : ''}${moeda(l.valor)}</strong>` }
  ], lista, 'Nenhum lançamento nos últimos 30 dias.');

  el.querySelector('#novo').onclick = () => formLancamento(checar(cats), checar(cts), estado,
    () => pagFinanceiro(el.closest('main'), estado));

  const bt = el.querySelector('#triagem');
  if (bt) bt.onclick = () => triagem(lista.filter(l => !l.categoria_id), checar(cats), estado,
    () => pagFinanceiro(el.closest('main'), estado));
}

function formLancamento(cats, contas, estado, aoSalvar) {
  modal({
    titulo: 'Novo lançamento',
    campos: [
      { nome: 'data', label: 'Data', tipo: 'date', req: true, valor: new Date().toISOString().slice(0, 10) },
      { nome: 'tipo', label: 'Tipo', tipo: 'select', req: true,
        opcoes: [{ valor: 'entrada', texto: 'Entrada' }, { valor: 'saida', texto: 'Saída' }] },
      { nome: 'valor', label: 'Valor', tipo: 'number', step: '0.01', req: true },
      { nome: 'conta_id', label: 'Conta', tipo: 'select', req: true,
        opcoes: contas.map(c => ({ valor: c.id, texto: c.nome })) },
      { nome: 'categoria_id', label: 'Categoria', tipo: 'select',
        opcoes: [{ valor: '', texto: '— sem categoria —' },
                 ...cats.map(c => ({ valor: c.id, texto: `${c.nome} (${c.tipo})` }))] },
      { nome: 'descricao', label: 'Descrição', req: true, full: true },
      { nome: 'observacao', label: 'Observação', tipo: 'textarea', full: true }
    ],
    onSalvar: async (d) => {
      checar(await sb.from('lancamentos').insert({
        ...d,
        tenant_id: estado.perfil.tenant_id,
        valor: Number(d.valor),
        criado_por: estado.perfil.id,
        origem: 'manual'
      }));
      aoSalvar();
    }
  });
}

/** Triagem em lote — o jeito de matar os 842 lançamentos sem categoria. */
function triagem(pendentes, cats, estado, aoSalvar) {
  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';
  fundo.innerHTML = `
    <div class="modal" style="max-width:900px">
      <header><h3>${icon('alerta')} Categorizar em lote</h3>
        <button class="icone" data-fechar>${icon('erro')}</button></header>
      <div class="corpo">
        <div class="aviso info">${icon('alerta')}
          <div>Escolha a categoria de cada linha. Enquanto houver lançamento sem categoria,
          o resultado por prédio e o lucro operacional ficam errados.</div></div>
        <table><thead><tr><th>Data</th><th>Descrição</th><th class="num">Valor</th><th>Categoria</th></tr></thead>
        <tbody>
          ${pendentes.slice(0, 50).map(l => `
            <tr>
              <td>${data(l.data)}</td>
              <td>${escapeHtml(l.descricao)}</td>
              <td class="num">${moeda(l.valor)}</td>
              <td><select data-lanc="${l.id}">
                <option value="">—</option>
                ${cats.filter(c => c.tipo === l.tipo)
                      .map(c => `<option value="${c.id}">${escapeHtml(c.nome)}</option>`).join('')}
              </select></td>
            </tr>`).join('')}
        </tbody></table>
      </div>
      <footer>
        <button data-fechar>Cancelar</button>
        <button class="primaria" id="salvar">Salvar categorias</button>
      </footer>
    </div>`;

  fundo.querySelectorAll('[data-fechar]').forEach(b => b.onclick = () => fundo.remove());
  fundo.querySelector('#salvar').onclick = async () => {
    const selects = [...fundo.querySelectorAll('select[data-lanc]')].filter(s => s.value);
    for (const s of selects) {
      await sb.from('lancamentos').update({ categoria_id: s.value }).eq('id', s.dataset.lanc);
    }
    fundo.remove();
    aoSalvar();
  };
  document.body.appendChild(fundo);
}

// ─── Conciliação ──────────────────────────────────────────────────────
// O coração do produto para quem vem de planilha: casar o dinheiro que
// entrou com a competência que ele quita. Sugere; quem decide é a pessoa.
async function conciliar(el, estado) {
  const sug = await sb.rpc('sugerir_conciliacao', { p_tenant: estado.perfil.tenant_id });
  const lista = sug.data || [];

  const CONF = { alta: ['alta', 'verde'], media: ['média', 'ambar'], baixa: ['baixa', 'vermelho'] };

  el.innerHTML = `
    <div class="aviso info">${icon('alerta')}
      <div>O sistema <strong>sugere</strong> o casamento entre o dinheiro recebido e a competência —
      mas não decide sozinho. Baixa automática errada em aluguel custa relacionamento com o inquilino,
      e relacionamento é o ativo da imobiliária.</div></div>
    ` + tabela([
    { th: 'Recebido em', td: s => data(s.lanc_data) },
    { th: 'Descrição',   td: s => escapeHtml(s.lanc_descricao) },
    { th: 'Valor', cls: 'num', td: s => moeda(s.lanc_valor) },
    { th: 'Competência sugerida', td: s => escapeHtml(competencia(s.competencia)) },
    { th: 'Valor da cobrança', cls: 'num', td: s => moeda(s.cob_valor) },
    { th: 'Diferença', cls: 'num', td: s => {
        const d = Number(s.diferenca);
        if (Math.abs(d) <= 0.05) return badge('exato', 'verde');
        return `<span style="color:var(--${d < 0 ? 'ambar' : 'azul'})">${moeda(Math.abs(d))} ${d < 0 ? 'a menos' : 'a mais'}</span>`;
      } },
    { th: 'Confiança', td: s => { const [t, c] = CONF[s.confianca] || [s.confianca, 'cinza']; return badge(t, c); } },
    { th: '', cls: 'acoes', td: s =>
        `<button class="link" data-lanc="${s.lancamento_id}" data-cob="${s.cobranca_id}">Confirmar</button>` }
  ], lista, 'Nada para conciliar — todo dinheiro recebido já está amarrado a uma competência.');

  el.querySelectorAll('[data-lanc]').forEach(b => b.onclick = async () => {
    const { error } = await sb.rpc('conciliar_lancamento', {
      p_lancamento: b.dataset.lanc, p_cobranca: b.dataset.cob
    });
    if (error) return alert(error.message);
    conciliar(el, estado);
  });
}

// ─── Contas ───────────────────────────────────────────────────────────
async function contas(el, estado) {
  const lista = checar(await sb.from('contas_financeiras').select('*').order('nome'));

  el.innerHTML = `
    <button class="primaria" id="nova" style="margin-bottom:16px">${icon('novo')} Nova conta</button>
    ` + tabela([
    { th: 'Conta', td: c => `<strong>${escapeHtml(c.nome)}</strong>` },
    { th: 'Tipo',  td: c => escapeHtml(c.tipo) },
    { th: 'Banco', td: c => escapeHtml(c.banco || '—') },
    { th: 'Chave Pix', td: c => c.pix_chave
        ? escapeHtml(c.pix_chave) : badge('sem Pix — não gera cobrança', 'ambar') },
    { th: 'Situação', td: c => badge(c.ativa ? 'ativa' : 'inativa', c.ativa ? 'verde' : 'cinza') }
  ], lista, 'Nenhuma conta cadastrada.');

  el.querySelector('#nova').onclick = () => modal({
    titulo: 'Nova conta de recebimento',
    aviso: 'A chave Pix da conta é a que vai na cobrança dos contratos ligados a ela. '
         + 'Sem chave, o contrato não gera Pix.',
    campos: [
      { nome: 'nome', label: 'Nome', req: true, full: true },
      { nome: 'tipo', label: 'Tipo', tipo: 'select',
        opcoes: ['banco', 'caixa', 'dinheiro', 'aplicacao'] },
      { nome: 'banco', label: 'Banco' },
      { nome: 'agencia', label: 'Agência' },
      { nome: 'conta', label: 'Conta' },
      { nome: 'pix_chave', label: 'Chave Pix', full: true },
      { nome: 'pix_nome_recebedor', label: 'Nome do recebedor no Pix', full: true,
        dica: 'É o nome que o inquilino vê no app do banco. Confira: nome errado = ninguém paga.' },
      { nome: 'pix_cidade', label: 'Cidade do recebedor' },
      { nome: 'saldo_inicial', label: 'Saldo inicial', tipo: 'number', step: '0.01' }
    ],
    onSalvar: async (d) => {
      d.saldo_inicial = d.saldo_inicial ? Number(d.saldo_inicial) : 0;
      checar(await sb.from('contas_financeiras').insert({ ...d, tenant_id: estado.perfil.tenant_id }));
      pagFinanceiro(el.closest('main'), estado);
    }
  });
}

// ─── Desempenho ───────────────────────────────────────────────────────
async function desempenho(el) {
  const lista = checar(await sb.from('vw_desempenho_imovel')
    .select('*').order('rendimento_liquido_pct', { ascending: false, nullsFirst: false }));

  el.innerHTML = `
    <div class="aviso info">${icon('alerta')}
      <div>A taxa de ocupação é <strong>calculada a partir dos contratos</strong> — não é um campo
      que alguém precise lembrar de atualizar.</div></div>
    ` + tabela([
    { th: 'Imóvel', td: i => `<strong>${escapeHtml(i.codigo)}</strong>` },
    { th: 'Prédio', td: i => escapeHtml(i.predio || '—') },
    { th: 'Ocupação (12m)', cls: 'num', td: i => {
        const p = Number(i.taxa_ocupacao_pct || 0);
        return badge(`${p}%`, p >= 90 ? 'verde' : p >= 60 ? 'ambar' : 'vermelho');
      } },
    { th: 'Recebido (12m)', cls: 'num', td: i => moeda(i.recebido_12m) },
    { th: 'Despesa (12m)', cls: 'num', td: i => moeda(i.despesa_12m) },
    { th: 'Valor de mercado', cls: 'num', td: i => i.valor_mercado ? moeda(i.valor_mercado) : '—' },
    { th: 'Rendimento líquido', cls: 'num', td: i => i.rendimento_liquido_pct != null
        ? `<strong>${i.rendimento_liquido_pct}% a.a.</strong>`
        : badge('falta valor de mercado', 'cinza') }
  ], lista, 'Sem dados ainda.');
}
